import { API } from "@swivl-mono/api-helper";
import { ConvertMessageToGPTLine, warn } from "@swivl-mono/helpers";
import { MessageInterface, MessageType, PromptInterface, ReviewInterface, Slug, UserInterface } from "@swivl-mono/types";
import { create } from "zustand";

import { Member } from "../Member/Member.Current";

interface StoreInterface {
  currentConversationAIAutocompleteUserId?: number;

  isLoadingAutoCompleteText: boolean;

  agentAutoCompleteText: string;

  generatedReviewSummaries: { [key: string]: string | undefined };
  isLoadingReviewSummary: boolean;
  agentAutoCompleteTranscriptPrompt?: string;

  prompts?: PromptInterface[];
}

const BlankState: StoreInterface = {
  isLoadingAutoCompleteText: false,
  agentAutoCompleteText: "",
  agentAutoCompleteTranscriptPrompt: undefined,
  prompts: undefined,
  generatedReviewSummaries: {},
  isLoadingReviewSummary: false
};

const LINES_LIMIT = 5;

// function convertMessageToGPTLine(message:Message):string {
//     if (message.type !== MessageType.Text && message.type !== MessageType.Buttons) {
//         return "";
//     }
//     let type;
//     if (message.from && message.from.type) {
//         if (message.from.type === SenderOrRecipientType.UserInterface) {
//             type = "User";
//         } else if (message.from.type === SenderOrRecipientType.Bot) {
//             type = "Bot";
//         } else if (message.from.type === SenderOrRecipientType.Agent) {
//             type = "Agent";
//         }
//     }

//     if (!type) {
//         return "";
//     }

//     if (message.type === MessageType.Text) {
//         const payload:TextPayloadInterface = message.payload as any;
//             return type + ": " + payload.text  + "\n";

//     } else if (message.type === MessageType.Buttons) {
//         if (message.from && message.from.type && message.actionTaken) {
//             return type + ": " + message.actionTaken.target  + "\n";
//         }
//     }
//     return "";
// }

export class AIModel {
  static useState = create<StoreInterface>((set) => ({ ...BlankState }));
  static setState = AIModel.useState.setState;
  static get state() {
    return AIModel.useState.getState();
  }
  static reset() {
    AIModel.setState({ ...BlankState });
  }

  static onConversationTranscriptUpdate(items: MessageInterface[], userId: number) {
    let output: any = items;
    output = output.filter((item: MessageInterface) => {
      if (item.type === MessageType.Typing) {
        return false;
      }
      return true;
    });
    if (output.length > LINES_LIMIT) {
      output = output.slice(0, LINES_LIMIT);
    }

    output.reverse();

    output = output.map((item: MessageInterface) => ConvertMessageToGPTLine(item)).filter((item: string) => item.length > 0);

    if (output.length > 0) {
      if (output[output.length - 1].startsWith("User:")) {
        const agentAutoCompleteTranscriptPrompt = output.join("");
        if (AIModel.state.agentAutoCompleteTranscriptPrompt !== agentAutoCompleteTranscriptPrompt) {
          AIModel.setState({ agentAutoCompleteTranscriptPrompt });

          AIModel.loadAgentAutoCompleteText(agentAutoCompleteTranscriptPrompt);
        } else {
        }
      } else {
        if (AIModel.state.agentAutoCompleteText.length > 0) {
          AIModel.setState({ agentAutoCompleteText: "", agentAutoCompleteTranscriptPrompt: undefined });
        } else {
        }
      }
    }
  }

  static autoCompleteCache = new Map<string, string>();
  static loadAgentAutoCompleteText(prompt: string) {
    if (AIModel.autoCompleteCache.has(prompt)) {
      AIModel.setState({ agentAutoCompleteText: AIModel.autoCompleteCache.get(prompt) });
      return;
    }
    AIModel.setState({ isLoadingAutoCompleteText: true });
    API.AI.predictAutoCompleteText(Member.orgId!, prompt)
      .then((result: string) => {
        AIModel.autoCompleteCache.set(prompt, result);
        AIModel.setState({
          agentAutoCompleteText: result,
          isLoadingAutoCompleteText: false
        });
      })
      .catch((error: any) => {
        warn("%% Error loading agent auto complete text", error);
      });
  }

  static generateReviewReply(reviewText: string, rating: number, reviewerName?: string, reviewerIsAnonymous?: boolean, locationId?: number) {
    return API.AI.generateReviewReply(Member.orgId!, reviewText, rating, reviewerName, reviewerIsAnonymous, locationId);
  }

  // static loadPrompts(force: boolean = false) {
  //   if (AIModel.state.prompts && !force) {
  //     return Promise.resolve(AIModel.state.prompts);
  //   }

  //   return API.AI.getAllPrompts(Member.orgId!)
  //     .then((prompts: PromptInterface[]) => {
  //       AIModel.setState({ prompts });
  //       return prompts;
  //     })
  //     .catch((error: any) => {
  //       warn("Error loading prompts", error);
  //     });
  // }

  static nameForPromptById(id: number) {
    const prompt = AIModel.state.prompts?.find((item) => item.id === id);
    return prompt?.name || "";
  }

  static loadPrompts(force: boolean = false) {
    if (AIModel.state.prompts && !force) {
      return Promise.resolve(AIModel.state.prompts);
    }
    return API.AI.getAllPrompts(Member.orgId!)
      .then((prompts: PromptInterface[]) => {
        AIModel.setState({ prompts });
        return prompts;
      })
      .catch((error: any) => {
        warn("Error loading prompts", error);
      });
  }

  static savePrompt(prompt: Partial<PromptInterface>) {
    return API.AI.savePrompt(Member.orgId!, prompt).then((_: PromptInterface) => {
      return AIModel.loadPrompts(true);
    });
  }

  static deletePrompt(prompt: PromptInterface) {
    return API.AI.deletePrompt(Member.orgId!, prompt.slug).then((_: boolean) => {
      return AIModel.loadPrompts(true);
    });
  }
  static nameForPrompt(slug: Slug) {
    const prompt = AIModel.state.prompts?.find((item) => item.slug === slug);
    return prompt?.name || "";
  }

  static async generateReviewSummary(
    orgId: number,
    reviews: ReviewInterface[],
    locationNames: { [key: number]: string },
    startDate?: string,
    endDate?: string
  ): Promise<string | null> {
    const cacheKey = `${orgId}-${startDate || "no-start"}-${endDate || "no-end"}-${reviews.length}`;

    // Check cache first
    if (AIModel.state.generatedReviewSummaries[cacheKey] && AIModel.state.generatedReviewSummaries[cacheKey] !== "loading") {
      return AIModel.state.generatedReviewSummaries[cacheKey] || null;
    }

    // Set loading state
    AIModel.setState({
      generatedReviewSummaries: {
        ...AIModel.state.generatedReviewSummaries,
        [cacheKey]: "loading"
      },
      isLoadingReviewSummary: true
    });

    try {
      const summary = await API.AI.generateReviewSummary(orgId, reviews, locationNames, startDate, endDate);
      AIModel.setState({
        generatedReviewSummaries: {
          ...AIModel.state.generatedReviewSummaries,
          [cacheKey]: summary
        },
        isLoadingReviewSummary: false
      });
      return summary;
    } catch (error) {
      warn("Error generating review summary", error);
      AIModel.setState({
        generatedReviewSummaries: {
          ...AIModel.state.generatedReviewSummaries,
          [cacheKey]: undefined
        },
        isLoadingReviewSummary: false
      });
      throw error;
    }
  }

  static getReviewSummary(orgId: number, startDate?: string, endDate?: string, reviewCount?: number): string | undefined {
    const cacheKey = `${orgId}-${startDate || "no-start"}-${endDate || "no-end"}-${reviewCount || 0}`;
    return AIModel.state.generatedReviewSummaries[cacheKey];
  }
  static setCurrentConversationAIAutocompleteUserId(userId?: number) {
    AIModel.setState({ currentConversationAIAutocompleteUserId: userId });
  }
}
